import {
	useRef, useState, useCallback, useEffect, ReactNode, ElementType
} from 'react';
import MinimizeIcon from '@mui/icons-material/Remove';
import OpenInFullIcon from '@mui/icons-material/OpenInFull';
import CloseIcon from '@mui/icons-material/Close';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { useRecoilState } from 'recoil';
import { useDraggable } from '@hooks/useDraggable';
import { useResizable } from '@hooks/useResizable';
import { Button } from '@components/FORM/Button/Button';
import { Caption } from '@components/CORE/Text/Text';
import { BUTTON_TYPES, BUTTON_SIZE, TEXT_WEIGHT } from '@types';
import { ActiveModuleId, MinimisedModules } from '@state';
import styles from './ModulePanel.module.scss';

const Z_ACTIVE = 100000;
const Z_INACTIVE = 99999;

const DOCK_WIDTH = 300;
const DOCK_GAP = 8;
const DOCK_BOTTOM = 8;

type SavedStyles = {
	left: string; top: string; right: string; bottom: string;
	width: string; height: string; transform: string;
};

type ModulePanelProps = {
	title: string;
	Icon: ElementType;
	onClose: () => void;
	onRun?: () => void;
	isRunning?: boolean;
	moduleId: string;
	/** Optional CSS class applied to the root panel div — use for per-module position overrides */
	panelClassName?: string;
	children: ReactNode;
};

export const ModulePanel = ({
	title,
	Icon,
	onClose,
	onRun,
	isRunning,
	moduleId,
	panelClassName,
	children,
}: ModulePanelProps) => {
	const headerRef = useRef<HTMLDivElement>(null);
	const panelRef = useRef<HTMLDivElement | null>(null);
	const minimisedRef = useRef(false);
	const savedStylesRef = useRef<SavedStyles | null>(null);

	const [minimised, setMinimisedState] = useState(false);
	const [activeId, setActiveId] = useRecoilState(ActiveModuleId);
	const [minimisedModules, setMinimisedModules] = useRecoilState(MinimisedModules);

	const isActive = activeId === moduleId;

	// Keep a ref in sync so useDraggable's closure can read it without stale capture
	const setMinimised = useCallback((val: boolean) => {
		minimisedRef.current = val;
		setMinimisedState(val);
	}, []);

	const { elementRef: draggableRef } = useDraggable(headerRef, { getDisabled: () => minimisedRef.current, });
	const { elementRef: resizableRef, resizeHandleProps } = useResizable({ minWidth: 600, minHeight: 250 });

	const setPanelRef = useCallback((el: HTMLDivElement | null) => {
		panelRef.current = el;
		(draggableRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
		(resizableRef as React.MutableRefObject<HTMLDivElement | null>).current = el;
	}, []);

	// Remove from dock on unmount (e.g. closed while minimised)
	useEffect(() => {
		return () => {
			setMinimisedModules((prev) => prev.filter((id) => id !== moduleId));
		};
	}, [moduleId, setMinimisedModules]);

	// Clear active state on click-outside (capture fires before panel mousedown)
	useEffect(() => {
		const handleDocClick = () => setActiveId(null);
		document.addEventListener('mousedown', handleDocClick, true);
		return () => document.removeEventListener('mousedown', handleDocClick, true);
	}, [setActiveId]);

	const handlePanelMouseDown = useCallback(() => {
		setActiveId(moduleId);
	}, [moduleId, setActiveId]);

	// Update z-index imperatively so it persists after click-outside
	useEffect(() => {
		const el = panelRef.current;
		if (!el) return;
		if (isActive) {
			el.style.zIndex = String(Z_ACTIVE);
		} else if (activeId !== null) {
			el.style.zIndex = String(Z_INACTIVE);
		}
	}, [isActive, activeId]);

	const handleMinimise = useCallback(() => {
		const el = panelRef.current;
		if (!el) return;

		if (!minimised) {
			// Snapshot current inline styles before collapsing
			savedStylesRef.current = {
				left: el.style.left,
				top: el.style.top,
				right: el.style.right,
				bottom: el.style.bottom,
				width: el.style.width,
				height: el.style.height,
				transform: el.style.transform,
			};
			setMinimised(true);
			setMinimisedModules((prev) => [...prev, moduleId]);
		} else {
			setMinimised(false);
			setMinimisedModules((prev) => prev.filter((id) => id !== moduleId));

			// Restore saved inline styles (empty string = revert to CSS class default)
			if (savedStylesRef.current) {
				const s = savedStylesRef.current;
				el.style.left = s.left;
				el.style.top = s.top;
				el.style.right = s.right;
				el.style.bottom = s.bottom;
				el.style.width = s.width;
				el.style.height = s.height;
				el.style.transform = s.transform;
			}
		}
	}, [minimised, moduleId, setMinimised, setMinimisedModules]);

	useEffect(() => {
		const el = panelRef.current;
		if (!el || !minimised) return;

		const index = minimisedModules.indexOf(moduleId);
		if (index === -1) return;

		// Stack from the right edge leftward
		const right = DOCK_GAP + index * (DOCK_WIDTH + DOCK_GAP);
		el.style.left = 'auto';
		el.style.top = 'auto';
		el.style.transform = 'none';
		el.style.width = `${DOCK_WIDTH}px`;
		el.style.height = 'auto';
		el.style.right = `${right}px`;
		el.style.bottom = `${DOCK_BOTTOM}px`;
	}, [minimised, minimisedModules, moduleId]);

	const panelClass = [styles.panel, panelClassName].filter(Boolean).join(' ');
	const headerClass = [styles.header, !isActive ? styles.headerInactive : ''].filter(Boolean).join(' ');

	return (
		<div ref={setPanelRef} className={panelClass} onMouseDown={handlePanelMouseDown}>
			<div ref={headerRef} className={headerClass}>
				<div className={`${styles.headerTitle} ${minimised ? styles.headerTitleMinimised : ''}`}>
					<Icon fontSize='small' />
					<Caption weight={TEXT_WEIGHT.SEMI_BOLD} color='var(--neutral-00)'>{title}</Caption>
				</div>
				<div className={styles.headerActions}>
					{!minimised && onRun && (
						<Button
							text={isRunning ? 'Running…' : 'Run'}
							onClick={onRun}
							type={BUTTON_TYPES.SECONDARY}
							size={BUTTON_SIZE.SMALL}
							Icon={PlayArrowIcon}
							disabled={isRunning}
							inverse
						/>
					)}
					<button className={styles.iconBtn} onClick={handleMinimise} aria-label={minimised ? 'Restore' : 'Minimise'}>
						{minimised ? <OpenInFullIcon fontSize='small' /> : <MinimizeIcon fontSize='small' />}
					</button>
					<button className={styles.iconBtn} onClick={onClose} aria-label='Close'>
						<CloseIcon fontSize='small' />
					</button>
				</div>
			</div>
			{!minimised && <div className={styles.body}>{children}</div>}
			{!minimised && <div className={styles.resizeHandle} {...resizeHandleProps} />}
		</div>
	);
};
